/// <amd-module name="Repos/Views/ProjectPolicies/Components/ProjectPoliciesList" />
import * as React from "react";
import * as Resources from "Repos/Views/ProjectPolicies/Resources";
import { VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { ListSelection } from "VSSUI/List";
import { Link } from "VSSUI/Link";
import { ITableColumn, SimpleTableCell, Table } from "VSSUI/Table";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { IProjectPoliciesService, IProjectPolicyItem } from "../Contracts";
import { findPolicyTypeName } from "../PolicyTypeNames";
import { transformScopeForDisplay } from "../ReadableScope";

export interface IProjectPoliciesListProps {
    hasEditPermission: boolean;
    projectPolicies: IProjectPolicyItem[];
    projectPoliciesService: IProjectPoliciesService;
    deleteScopeOnClick: (scope: string) => void;
}

export class ProjectPoliciesList extends VssComponent<IProjectPoliciesListProps> {
    private selection = new ListSelection({ multiSelect: false });

    public render() {
        const { projectPolicies } = this.props;

        const itemProvider = new ArrayItemProvider(projectPolicies);

        return (
            <Table<IProjectPolicyItem>
                columns={[this.branchColumn, this.checksColumn, this.editColumn, this.deleteColumn]}
                selection={this.selection}
                showHeader={true}
                showLines={true}
                itemProvider={itemProvider}
            />
        );
    }

    private readonly branchColumn: ITableColumn<IProjectPolicyItem> = {
        id: "branch",
        name: Resources.ColumnBranchPatternHeader,
        minWidth: 150,
        maxWidth: 500,
        width: -30,

        renderCell: (rowIndex, columnIndex, tableColumn, tableItem) => (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                {transformScopeForDisplay(tableItem.scope)}
            </SimpleTableCell>
        )
    };

    private readonly checksColumn: ITableColumn<IProjectPolicyItem> = {
        id: "checks",
        name: Resources.ColumnChecksHeader,
        minWidth: 250,
        width: -70,

        renderCell: (rowIndex, columnIndex, tableColumn, tableItem) => (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                {tableItem.policyTypeIds.map(findPolicyTypeName).join(", ")}
            </SimpleTableCell>
        )
    };

    private readonly editColumn: ITableColumn<IProjectPolicyItem> = {
        id: "edit",
        name: "",
        width: 42,

        renderCell: (rowIndex, columnIndex, tableColumn, tableItem) => (
            <SimpleTableCell
                columnIndex={columnIndex}
                key={columnIndex}
                tableColumn={tableColumn}
                contentClassName={"justify-center bolt-table-cell-side-action"}
            >
                <EditButton rowIndex={rowIndex} scope={tableItem.scope} disabled={!this.props.hasEditPermission} />
            </SimpleTableCell>
        )
    };

    private readonly deleteColumn: ITableColumn<IProjectPolicyItem> = {
        id: "del",
        name: "",
        width: 42,

        renderCell: (rowIndex, columnIndex, tableColumn, tableItem) => (
            <SimpleTableCell
                columnIndex={columnIndex}
                key={columnIndex}
                tableColumn={tableColumn}
                contentClassName={"justify-center bolt-table-cell-side-action"}
            >
                <DeleteButton rowIndex={rowIndex} onClick={this.deleteOnClick} disabled={!this.props.hasEditPermission} />
            </SimpleTableCell>
        )
    };

    private readonly deleteOnClick = (event: buttonClickEvent): void => {
        const dataIndex = event.currentTarget.dataset["index"];

        if (dataIndex !== undefined) {
            const rowIndex: number = +dataIndex | 0;

            const scope = this.props.projectPolicies[rowIndex].scope;

            this.props.deleteScopeOnClick(scope);
        }
    };
}

type buttonClickEvent = React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;

interface IEditButtonProps {
    rowIndex: number;
    scope: string;
    disabled: boolean;
}

const EditButton: React.FunctionComponent<IEditButtonProps> = props => {
    const { rowIndex, scope, disabled } = props;

    let refName: string;

    if (scope.startsWith("*:")) {
        // should always be true
        refName = scope.substring(2);
    } else {
        refName = scope;
    }

    if (refName != "~default") {
        refName = encodeURIComponent(refName)
            .replace(/%3A/gi, ":")
            .replace(/%2F/gi, "/")
            .replace(/%42/gi, "*");
    }

    return (
        <Button
            ariaLabel={Resources.Edit}
            dataIndex={rowIndex}
            className="bolt-table-cell-content-reveal"
            iconProps={{ iconName: "Edit" }}
            href={"?refName=" + refName}
            disabled={disabled}
            tooltipProps={disabled ? { text: Resources.EditProjectPolicy_NoPermission } : undefined}
            subtle={true}
        />
    );
};

interface IDeleteButtonProps {
    rowIndex: number;
    onClick: (event: buttonClickEvent) => void;
    disabled: boolean;
}

const DeleteButton: React.FunctionComponent<IDeleteButtonProps> = props => {
    const { rowIndex, onClick, disabled } = props;

    return (
        <Button
            ariaLabel={Resources.Delete}
            dataIndex={rowIndex}
            className="bolt-table-cell-content-reveal"
            iconProps={{ iconName: "Delete" }}
            onClick={onClick}
            disabled={disabled}
            tooltipProps={disabled ? { text: Resources.EditProjectPolicy_NoPermission } : undefined}
            subtle={true}
        />
    );
};
